import { Colors } from '@/api';
import { useDialogStore, type DialogOptions } from '@/store/dialogStore';

interface ProgrammaticDialogOptions {
  title?: string;
  message: string;
  okLabel?: string;
  cancelLabel?: string;
  hideCancelButton?: boolean;
  color?: Colors;
  icon?: string;
  width?: string;
  html?: boolean; // Message is rendered as HTML but sanitized with DOMPurify
  onOk?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
}

export function showProgrammaticDialog(options: ProgrammaticDialogOptions): {
  onOk: (callback: () => void | Promise<void>) => {
    onCancel: (callback: () => void | Promise<void>) => void;
  };
  onCancel: (callback: () => void | Promise<void>) => void;
} {
  const dialogStore = useDialogStore();

  // Store callbacks that can be set via chained API
  let okCallback: (() => void | Promise<void>) | null = options.onOk || null;
  let cancelCallback: (() => void | Promise<void>) | null =
    options.onCancel || null;

  // Build dialog options with wrapped callbacks
  const dialogOptions: DialogOptions = {
    title: options.title,
    message: options.message,
    okLabel: options.okLabel,
    cancelLabel: options.cancelLabel,
    hideCancelButton: options.hideCancelButton,
    color: options.color,
    icon: options.icon,
    width: options.width,
    onOk: async () => {
      if (okCallback) {
        await okCallback();
      }
    },
    onCancel: async () => {
      if (cancelCallback) {
        await cancelCallback();
      }
    },
  };

  // Get current active element as trigger for focus restoration
  const triggerElement = document.activeElement as HTMLElement | undefined;

  // Show dialog via store
  dialogStore.show(dialogOptions, triggerElement);

  // Return chainable API for backward compatibility with old dialog system
  return {
    onOk: (callback: () => void | Promise<void>) => {
      okCallback = callback;
      // Update the store's options with the new callback
      if (dialogStore.options) {
        dialogStore.options.onOk = async () => {
          if (okCallback) {
            await okCallback();
          }
        };
      }
      return {
        onCancel: (cancelCb: () => void | Promise<void>) => {
          cancelCallback = cancelCb;
          if (dialogStore.options) {
            dialogStore.options.onCancel = async () => {
              if (cancelCallback) {
                await cancelCallback();
              }
            };
          }
        },
      };
    },
    onCancel: (callback: () => void | Promise<void>) => {
      cancelCallback = callback;
      if (dialogStore.options) {
        dialogStore.options.onCancel = async () => {
          if (cancelCallback) {
            await cancelCallback();
          }
        };
      }
    },
  };
}
